const TOKEN_KEY = 'orderflow.token';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (token: string) => localStorage.setItem(TOKEN_KEY, token);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

interface Options {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
}

export async function api<T>(path: string, options: Options = {}): Promise<T> {
  const url = new URL(path, window.location.origin);
  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value));
  }

  const token = getToken();
  const response = await fetch(url.toString(), {
    method: options.method ?? 'GET',
    headers: {
      ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });

  if (response.status === 401 && !path.endsWith('/login')) {
    clearToken();
    window.location.href = '/login';
    throw new ApiError(401, 'Sitzung abgelaufen');
  }

  const text = await response.text();
  const payload = text ? (JSON.parse(text) as Record<string, unknown>) : {};
  if (!response.ok) {
    throw new ApiError(
      response.status,
      typeof payload.error === 'string' ? payload.error : 'Anfrage fehlgeschlagen',
      payload.details,
    );
  }
  return payload as T;
}

/** Fehlertext inklusive Feldhinweisen, wie er in der Oberfläche erscheint. */
export function errorText(error: unknown): string {
  if (error instanceof ApiError) {
    if (Array.isArray(error.details) && error.details.length) {
      return `${error.message}: ${error.details.map((d: { feld: string; problem: string }) => `${d.feld} – ${d.problem}`).join(', ')}`;
    }
    return error.message;
  }
  return error instanceof Error ? error.message : 'Unbekannter Fehler';
}

const berlin = { timeZone: 'Europe/Berlin' } as const;

export const formatDateTime = (value: string | null | undefined) =>
  value
    ? new Date(value).toLocaleString('de-DE', { ...berlin, day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })
    : '—';

/** "heute 14:30", "morgen 09:00", "vor 3 Std." - für Fristen lesbarer als ein Datum. */
export function formatFrist(value: string): string {
  const d = new Date(value);
  const jetzt = new Date();
  const diffMin = Math.round((d.getTime() - jetzt.getTime()) / 60_000);
  const uhr = d.toLocaleTimeString('de-DE', { ...berlin, hour: '2-digit', minute: '2-digit' });
  const tag = (x: Date) => x.toLocaleDateString('de-DE', berlin);
  if (diffMin < 0) {
    const vor = -diffMin;
    if (vor < 60) return `seit ${vor} Min.`;
    if (vor < 48 * 60) return `seit ${Math.round(vor / 60)} Std.`;
    return `seit ${Math.round(vor / 1440)} Tagen`;
  }
  if (tag(d) === tag(jetzt)) return `heute ${uhr}`;
  const morgen = new Date(jetzt.getTime() + 86_400_000);
  if (tag(d) === tag(morgen)) return `morgen ${uhr}`;
  return d.toLocaleString('de-DE', { ...berlin, weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

/** Wert für <input type="datetime-local"> in deutscher Ortszeit. */
export function alsLokalesDatum(d: Date): string {
  const versatz = d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - versatz).toISOString().slice(0, 16);
}

export const formatGeld = (cent: number | null | undefined) =>
  cent === null || cent === undefined ? '—' : new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cent / 100);

/** "12,50" oder "12.50" → 1250 Cent; leer → null. */
export function centAusText(text: string): number | null {
  const t = text.trim().replace(/\s|€/g, '').replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.');
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
}

export const formatDate = (value: string | null | undefined) => {
  if (!value) return '—';
  const plain = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (plain) return `${plain[3]}.${plain[2]}.${plain[1]}`;
  return new Date(value).toLocaleDateString('de-DE', { timeZone: 'Europe/Berlin' });
};
